import React, { useState, useEffect, useRef } from "react";
import { authHeaders } from '../lib/apiAuth';
import { T, WRITING_PROMPTS, WRITING_CRITERIA, EXAM_WRITING, WRITING_TYPE_LABELS } from './shared/constants';
import { ProgressBar } from './shared/ui';

// A handful of prompts per type at a time from the year's full set (public/naplan/writing/<year>.json, hundreds of
// NAPLAN-style prompts, merged with the built-in ones); "Show different prompts" deals a fresh handful.
const PER_TYPE = 4;
function deal(list, n) { return [...list].sort(() => Math.random() - 0.5).slice(0, n); }

// Writing test (exam mode): one prompt, timed like the real test, no tips, then marked on the 10 criteria.
// Official NAPLAN writing test length (nap.edu.au, test window): Year 3 40 minutes, Years 5, 7 and 9 42 minutes.
const TEST_MINUTES = { y3: 40, y5: 42, y7: 42, y9: 42 };
const EXAM_MIN_WORDS = 20;
const mmss = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

// paper: the official writing paper (src/lib/examStructure.js) when known; onResult/doneLabel: set when this is one
// paper of a full mock exam — the score is reported back and "Done" moves on to the next paper.
export default function WritingPractice({ yearId, onClose, serverUrl, exam = false, paper = null, onResult = null, doneLabel = null, examProgram = "naplan" }) {
  // other exams (NSW Selective, VIC Selective) have their own prompts, task types and marking criteria
  const ex = examProgram !== "naplan" ? EXAM_WRITING[examProgram] : null;
  const builtIn = ex ? [] : (WRITING_PROMPTS[yearId] || WRITING_PROMPTS.y5);
  const dealShown = (list) => ex ? deal(list, PER_TYPE * 2) : [...deal(list.filter(p => p.type === "narrative"), PER_TYPE), ...deal(list.filter(p => p.type === "persuasive"), PER_TYPE)];
  const typeLabel = (t) => WRITING_TYPE_LABELS[t] || t;
  const [allPrompts, setAllPrompts] = useState(builtIn);
  const [shown, setShown] = useState(() => dealShown(builtIn));
  useEffect(() => {
    let live = true;
    fetch(ex ? `/exam/writing/${examProgram}-${yearId}.json` : `/naplan/writing/${yearId || "y5"}.json`).then(r => (r.ok ? r.json() : [])).then(list => {
      if (!live || !Array.isArray(list) || !list.length) return;
      const merged = [...builtIn, ...list];
      setAllPrompts(merged);
      setShown(dealShown(merged));
    }).catch(() => {});
    return () => { live = false; };
  }, [yearId, examProgram]); // eslint-disable-line react-hooks/exhaustive-deps
  const reshuffle = () => setShown(dealShown(allPrompts));
  const prompts = shown;
  const [step, setStep]         = useState("select");
  const [prompt, setPrompt]     = useState(null);
  const [essay, setEssay]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError]       = useState(null);
  const [endsAt, setEndsAt]     = useState(null);   // exam: when time runs out (ms)
  const [now, setNow]           = useState(Date.now());
  const [leaving, setLeaving]   = useState(false);  // exam: "leave the test?" confirmation showing
  const [timeUsed, setTimeUsed] = useState(null);
  const minWords = exam ? EXAM_MIN_WORDS : 50;
  const testMinutes = (ex?.minutesByYear && ex.minutesByYear[yearId]) || paper?.minutes || TEST_MINUTES[yearId] || 42; // ACER: one of the two tasks
  const wordCount = essay.trim() ? essay.trim().split(/\s+/).length : 0;

  function startTest() {
    // one prompt, narrative or persuasive at random, as in the real test
    const type = Math.random() < 0.5 ? "narrative" : "persuasive";
    const pool = ex ? allPrompts : allPrompts.filter(p => p.type === type); // other exams: any of their task types
    setPrompt(pool[Math.floor(Math.random() * pool.length)]);
    setEssay(""); setFeedback(null); setError(null); setLeaving(false);
    setEndsAt(Date.now() + testMinutes * 60000); setNow(Date.now());
    setStep("write");
  }

  // exam countdown; at zero the response is handed in as it stands
  const autoSubmitted = useRef(false);
  useEffect(() => {
    if (!exam || step !== "write" || !endsAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [exam, step, endsAt]);
  const secondsLeft = endsAt ? Math.max(0, Math.round((endsAt - now) / 1000)) : 0;
  const timeUp = exam && step === "write" && endsAt && secondsLeft === 0;
  useEffect(() => {
    if (!timeUp || autoSubmitted.current) return;
    autoSubmitted.current = true;
    if (wordCount >= minWords) getFeedback();
  }, [timeUp]); // eslint-disable-line react-hooks/exhaustive-deps

  async function getFeedback() {
    if (wordCount < minWords) return;
    if (exam && endsAt) setTimeUsed(Math.min(testMinutes * 60, Math.round((Date.now() - (endsAt - testMinutes * 60000)) / 1000)));
    setLoading(true);
    setError(null);
    const criteria = ex ? ex.criteria : WRITING_CRITERIA[prompt.type];
    // marked by the server's marker (marking.js, mode 'writing'): Sonnet, every mark checked against its criterion's maximum, a tip
    // per criterion, a summary, two strengths and two priorities; the daily allowance and spending cap live there too
    const yr = yearId.replace('y', '');
    const question = `${ex ? `${ex.name} writing task` : 'NAPLAN writing task'}, Year ${yr}. Text type: ${prompt.type}.${ex ? ' Mark at the standard of a selective school entry test: an outstanding response for the student\'s age earns full marks, a typical school-level response earns about half.' : ''}
Prompt: "${prompt.prompt}"${prompt.stimulus ? `\nStimulus: "${prompt.stimulus}"` : ''}`;
    const strong = Array.isArray(prompt.criteria) && prompt.criteria.length ? `What a strong response to this prompt does:\n- ${prompt.criteria.join('\n- ')}` : '';
    try {
      const res = await fetch(`${serverUrl}/api/mark`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeaders()) },
        body: JSON.stringify({
          mode: 'writing', question, model: strong, answer: essay, level: `Year ${yr}`, subject: 'english', cmd: prompt.type,
          criteria: criteria.map(c => [`${c.label}: ${c.desc}`, c.max]),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) throw new Error('Please sign in again to have your writing marked.');
      if (res.status === 429) throw new Error('That is a lot of marking in one hour. Try again a little later.');
      if (!data.ok) throw new Error(data.reason || 'Lumi could not mark this right now. Please try again.');
      const r = data.result;
      setFeedback({
        summary: r.summary,
        scores: Object.fromEntries(criteria.map((c, i) => [c.id, { score: r.criteria[i].awarded, max: r.criteria[i].max, feedback: r.criteria[i].why, tip: r.criteria[i].tip }])),
        strengths: r.strengths,
        priorities: r.priorities,
      });
      setEndsAt(null); autoSubmitted.current = false;
      setStep("feedback");
    } catch (e) {
      setError(e && e.message && !/fetch|network|JSON/i.test(e.message) ? e.message : "Lumi had trouble connecting. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const criteria = prompt ? (ex ? ex.criteria : WRITING_CRITERIA[prompt.type]) : [];
  const totalScore  = feedback ? criteria.reduce((sum, c) => sum + (feedback.scores[c.id]?.score || 0), 0) : 0;
  const totalMax    = criteria.reduce((sum, c) => sum + c.max, 0);
  const totalPct    = totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : 0;
  const bandLabel   = totalPct >= 85 ? "Exceeding" : totalPct >= 65 ? "Strong" : totalPct >= 45 ? "Developing" : "Needs Support";
  const bandColor   = totalPct >= 85 ? T.green : totalPct >= 65 ? T.accent : totalPct >= 45 ? T.gold : T.red;
  // a mock exam's writing paper reports its mark once, as soon as it is marked
  const reported = useRef(false);
  useEffect(() => {
    if (!onResult || step !== "feedback" || !feedback || reported.current) return;
    reported.current = true;
    onResult({ score: totalScore, max: totalMax, band: bandLabel });
  }, [step, feedback]); // eslint-disable-line react-hooks/exhaustive-deps

  if (exam && step === "select") return (
    <div style={{ minHeight:"100%", background:T.bg }}>
      <div style={{ padding:"20px 24px", borderBottom:`1px solid ${T.border}`, background:T.surface, display:"flex", alignItems:"center", gap:12 }}>
        <button onClick={onClose} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, color:T.textMid, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>{"\u2190"}</button>
        <div>
          <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:18, color:T.text, margin:0 }}>{"\uD83D\uDCDD"} {ex ? ex.name : "NAPLAN Writing Test"}</h2>
          <p style={{ fontSize:12, color:T.textMid, margin:0 }}>Year {String(yearId || "y5").replace("y","")} · {testMinutes} minutes · marked on {ex ? `${ex.criteria.length} criteria` : "all 10 criteria"}</p>
        </div>
      </div>
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 24px 100px" }}>
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:"22px 24px" }}>
          <p style={{ fontSize:15, fontWeight:700, color:T.text, margin:"0 0 12px" }}>How the test works</p>
          <ul style={{ margin:0, paddingLeft:18, fontSize:14, color:T.textMid, lineHeight:1.8 }}>
            <li>{ex ? ex.howItWorks : <>You get <b>one</b> writing task — a story (narrative) or a persuasive piece. You won't know which until you start.</>}</li>
            <li>You have <b>{testMinutes} minutes</b>, just like the real test. Spend the first few minutes planning.</li>
            <li>No tips during the test. When time runs out, your writing is handed in as it is.</li>
            <li>Lumi then marks it on {ex ? `the ${ex.criteria.length} things the test looks for (${ex.criteria.map(c => c.label.toLowerCase()).join(", ")})` : "the 10 NAPLAN criteria"} and shows you what to work on.</li>
          </ul>
          {yearId === "y3" && (
            <p style={{ fontSize:13, color:T.textMid, margin:"14px 0 0", lineHeight:1.6 }}>In the real Year 3 test you write on paper. Try planning on paper first, then type your writing here.</p>
          )}
        </div>
        <button onClick={startTest} disabled={allPrompts.length === 0}
          style={{ width:"100%", marginTop:20, padding:14, background:T.accent, border:"none", borderRadius:12, color:"white", fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
          Start the {testMinutes}-minute test
        </button>
      </div>
    </div>
  );

  if (step === "select") return (
    <div style={{ minHeight:"100%", background:T.bg }}>
      <div style={{ padding:"20px 24px", borderBottom:`1px solid ${T.border}`, background:T.surface, display:"flex", alignItems:"center", gap:12 }}>
        <button onClick={onClose} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, color:T.textMid, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>{"\u2190"}</button>
        <div>
          <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:18, color:T.text, margin:0 }}>{"\u270D\uFE0F"} Writing Practice</h2>
          <p style={{ fontSize:12, color:T.textMid, margin:0 }}>{ex ? `AI feedback for the ${ex.name}` : "AI feedback on all 10 NAPLAN criteria"}</p>
        </div>
      </div>
      <div style={{ padding:"20px 24px 100px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20, flexWrap:"wrap" }}>
          <p style={{ fontSize:13, color:T.textMid, margin:0, flex:1, minWidth:200 }}>{ex ? "Choose a writing task. Lumi will mark your response on what the test looks for." : "Choose a writing prompt. Lumi will score your response against the real NAPLAN rubric."}</p>
          {allPrompts.length > PER_TYPE * 2 && (
            <button onClick={reshuffle} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"8px 12px", color:T.text, fontSize:12, fontWeight:600, cursor:"pointer" }}>
              🔀 Show different prompts ({allPrompts.length} in all)
            </button>
          )}
        </div>
        {(ex ? [...new Set(prompts.map(p => p.type))] : ["narrative","persuasive"]).map(type => (
          <div key={type} style={{ marginBottom:24 }}>
            <p style={{ fontSize:11, color:T.textMid, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:10 }}>
              {ex ? typeLabel(type) : type === "narrative" ? "\uD83D\uDCD6 Narrative" : "\uD83D\uDCAC Persuasive"}
            </p>
            {prompts.filter(p => p.type === type).map(p => (
              <div key={p.id} onClick={() => { setPrompt(p); setEssay(""); setStep("write"); }}
                style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:"16px 18px", marginBottom:10, cursor:"pointer", transition:"all .15s" }}
                onMouseOver={e => { e.currentTarget.style.borderColor = T.purple; e.currentTarget.style.background = T.purpleLo; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.card; }}>
                <p style={{ fontWeight:700, fontSize:14, color:T.text, marginBottom:4 }}>{p.title}</p>
                <p style={{ fontSize:12, color:T.textMid, lineHeight:1.5 }}>{p.prompt.substring(0,100)}...</p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  if (step === "write") return (
    <div style={{ minHeight:"100%", background:T.bg, display:"flex", flexDirection:"column" }}>
      <div style={{ padding:"16px 20px", borderBottom:`1px solid ${T.border}`, background:T.surface, display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
        <button onClick={() => (exam ? setLeaving(true) : setStep("select"))} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, color:T.textMid, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>{"\u2190"}</button>
        <div style={{ flex:1 }}>
          <p style={{ fontWeight:700, fontSize:14, color:T.text, margin:0 }}>{prompt.title}</p>
          <p style={{ fontSize:11, color:T.purple, margin:0, fontWeight:600 }}>{ex ? typeLabel(prompt.type) : prompt.type === "narrative" ? "\uD83D\uDCD6 Narrative" : "\uD83D\uDCAC Persuasive"}</p>
        </div>
        {exam && endsAt && (
          <span aria-label="Time left" style={{ fontSize:14, fontWeight:800, fontVariantNumeric:"tabular-nums", color: secondsLeft <= 300 ? T.red : T.text, background: secondsLeft <= 300 ? T.redLo : T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"4px 10px" }}>{"\u23F1"} {mmss(secondsLeft)}</span>
        )}
        <span style={{ fontSize:12, color:wordCount >= minWords ? T.green : T.textMid, fontWeight:600 }}>{wordCount}w</span>
      </div>
      {exam && leaving && (
        <div style={{ margin:"12px 20px 0", padding:"12px 14px", background:T.redLo, border:`1px solid ${T.red}44`, borderRadius:10, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <span style={{ flex:1, fontSize:13, color:T.text }}>Leave the test? Your writing will not be saved.</span>
          <button onClick={() => setLeaving(false)} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Keep writing</button>
          <button onClick={() => { setEndsAt(null); setLeaving(false); setStep("select"); }} style={{ background:T.red, color:"white", border:"none", borderRadius:8, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Leave test</button>
        </div>
      )}
      {timeUp && wordCount < minWords && (
        <div style={{ margin:"12px 20px 0", padding:"12px 14px", background:T.goldLo, border:`1px solid ${T.gold}55`, borderRadius:10, fontSize:13, color:T.text }}>
          Time's up! There isn't enough writing to mark yet ({wordCount} words). You can keep going and hand it in when you reach {minWords} words.
        </div>
      )}
      <div style={{ background:`${T.purple}11`, borderLeft:`3px solid ${T.purple}`, margin:"16px 20px 0", padding:"12px 14px", borderRadius:"0 8px 8px 0" }}>
        <p style={{ fontSize:13, color:T.text, lineHeight:1.6, margin:0 }}>{prompt.prompt}</p>
        {prompt.stimulus && <p style={{ fontSize:13, color:T.textMid, lineHeight:1.6, margin:"8px 0 0", fontStyle:"italic", whiteSpace:"pre-wrap" }}>{prompt.stimulus}</p>}
        {!exam && Array.isArray(prompt.tips) && prompt.tips.length > 0 && (
          <ul style={{ margin:"10px 0 0", paddingLeft:18, fontSize:12, color:T.textMid, lineHeight:1.6 }}>
            {prompt.tips.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        )}
      </div>
      <div style={{ flex:1, padding:"12px 20px 0", display:"flex", flexDirection:"column" }}>
        <textarea
          value={essay}
          onChange={e => setEssay(e.target.value)}
          placeholder="Start writing your response here..."
          style={{ flex:1, minHeight:320, background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:16, color:T.text, fontSize:14, lineHeight:1.7, fontFamily:"'Plus Jakarta Sans',sans-serif", resize:"none", outline:"none", width:"100%", boxSizing:"border-box" }}
          onFocus={e => e.target.style.borderColor = T.purple}
          onBlur={e => e.target.style.borderColor = T.border}
        />
      </div>
      <div style={{ padding:"16px 20px 32px", background:T.bg, borderTop:`1px solid ${T.border}` }}>
        {error && <p style={{ color:T.red, fontSize:13, marginBottom:10, textAlign:"center" }}>{error}</p>}
        {wordCount < minWords && (
          <p style={{ color:T.textMid, fontSize:12, marginBottom:10, textAlign:"center" }}>Write at least {minWords} words {exam ? "to hand it in" : "to get feedback"} ({minWords - wordCount} more needed)</p>
        )}
        <button
          onClick={getFeedback}
          disabled={wordCount < minWords || loading}
          style={{ width:"100%", padding:14, background: wordCount >= minWords && !loading ? "linear-gradient(135deg,#FBBF24,#F59E0B)" : T.card, border:"none", borderRadius:12, color: wordCount >= minWords && !loading ? T.bg : T.textMid, fontWeight:700, fontSize:15, cursor: wordCount >= minWords && !loading ? "pointer" : "not-allowed", fontFamily:"'Plus Jakarta Sans',sans-serif", transition:"all .2s" }}
        >
          {loading ? "\u2728 Lumi is reading your writing..." : exam ? "\uD83D\uDCE4 Hand in my writing" : "\u2728 Get Lumi's Feedback"}
        </button>
      </div>
    </div>
  );

  if (step === "feedback" && feedback) return (
    <div style={{ minHeight:"100%", background:T.bg }}>
      <div style={{ padding:"16px 20px", borderBottom:`1px solid ${T.border}`, background:T.surface, display:"flex", alignItems:"center", gap:12 }}>
        <button onClick={() => setStep("select")} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, color:T.textMid, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>{"\u2190"}</button>
        <div style={{ flex:1 }}>
          <p style={{ fontWeight:700, fontSize:14, color:T.text, margin:0 }}>Lumi's Feedback</p>
          <p style={{ fontSize:11, color:T.textMid, margin:0 }}>{prompt.title}{exam && timeUsed != null ? ` · Writing test · ${mmss(timeUsed)} of ${testMinutes}:00 used` : ""}</p>
        </div>
        <button onClick={onClose} style={{ background:T.accent, border:"none", borderRadius:8, padding:"6px 14px", color:"white", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{doneLabel || "Done \u2713"}</button>
      </div>
      <div style={{ padding:"20px 20px 100px" }}>
        <div style={{ background:`linear-gradient(135deg,${bandColor}22,${bandColor}11)`, border:`1px solid ${bandColor}44`, borderRadius:16, padding:"20px", marginBottom:20, textAlign:"center" }}>
          <div style={{ fontSize:48, fontFamily:"'Space Grotesk',sans-serif", color:bandColor, fontWeight:700 }}>{totalScore}<span style={{ fontSize:24, color:T.textMid }}>/{totalMax}</span></div>
          <div style={{ display:"inline-block", background:bandColor, color:T.bg, fontWeight:700, fontSize:13, padding:"4px 14px", borderRadius:99, marginTop:6 }}>{bandLabel}</div>
          <p style={{ fontSize:13, color:T.text, lineHeight:1.6, margin:"12px 0 0" }}>{feedback.summary}</p>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
          <div style={{ background:`${T.green}11`, border:`1px solid ${T.green}33`, borderRadius:12, padding:14 }}>
            <p style={{ fontSize:12, color:T.green, fontWeight:700, margin:"0 0 8px" }}>{"\u2705"} Strengths</p>
            {(feedback.strengths || []).map((s,i) => <p key={i} style={{ fontSize:12, color:T.text, margin:"0 0 4px", lineHeight:1.5 }}>{"\u00B7"} {s}</p>)}
          </div>
          <div style={{ background:`${T.gold}11`, border:`1px solid ${T.gold}33`, borderRadius:12, padding:14 }}>
            <p style={{ fontSize:12, color:T.gold, fontWeight:700, margin:"0 0 8px" }}>{"\uD83C\uDFAF"} Focus On</p>
            {(feedback.priorities || []).map((p,i) => <p key={i} style={{ fontSize:12, color:T.text, margin:"0 0 4px", lineHeight:1.5 }}>{"\u00B7"} {p}</p>)}
          </div>
        </div>
        <p style={{ fontSize:12, color:T.textMid, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:12 }}>Detailed Breakdown</p>
        {criteria.map(c => {
          const s = feedback.scores[c.id];
          if (!s) return null;
          const pct = Math.round((s.score / s.max) * 100);
          const col = pct >= 80 ? T.green : pct >= 60 ? T.accent : pct >= 40 ? T.gold : T.red;
          return (
            <div key={c.id} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:"14px 16px", marginBottom:10 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                <p style={{ fontWeight:700, fontSize:13, color:T.text, margin:0 }}>{c.label}</p>
                <span style={{ background:`${col}22`, color:col, fontWeight:700, fontSize:13, padding:"2px 10px", borderRadius:99 }}>{s.score}/{s.max}</span>
              </div>
              <ProgressBar value={s.score} max={s.max} color={col} height={4} />
              <p style={{ fontSize:13, color:T.text, margin:"8px 0 4px", lineHeight:1.5 }}>{s.feedback}</p>
              {s.tip && <p style={{ fontSize:12, color:T.gold, margin:0, lineHeight:1.5 }}>{"\uD83D\uDCA1"} {s.tip}</p>}
            </div>
          );
        })}
        {/* revise keeps the essay (it used to clear it, which made "revise" impossible); not offered inside a mock exam */}
        {!onResult && <button onClick={() => { setFeedback(null); setEndsAt(null); setStep("write"); }}
          style={{ width:"100%", padding:14, background:T.card, border:`1px solid ${T.border}`, borderRadius:12, color:T.text, fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", marginTop:8 }}>
          {"\u270F\uFE0F"} Revise & Resubmit
        </button>}
        {onResult ? (
          <button onClick={onClose} style={{ width:"100%", padding:14, background:T.accent, border:"none", borderRadius:12, color:"white", fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", marginTop:10 }}>{doneLabel}</button>
        ) : <button onClick={() => { setPrompt(null); setEssay(""); setFeedback(null); setTimeUsed(null); setStep("select"); }}
          style={{ width:"100%", padding:14, background:"linear-gradient(135deg,#FBBF24,#F59E0B)", border:"none", borderRadius:12, color:T.bg, fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", marginTop:10 }}>
          {exam ? "Take another writing test" : "Try Another Prompt"} {"\u2192"}
        </button>}
      </div>
    </div>
  );

  return null;
}
